import { useState, useEffect, useCallback, useRef } from 'react';
import { useDatabase } from '../contexts/DatabaseContext';
import { apiClient } from '../services/apiClient';
import { knowledgeApi, KnowledgeItem } from '../services/api';

/**
 * Результат автосохранения
 */
export interface AutoSaveResult {
  success: boolean;
  timestamp: number;
  message?: string;
}

/**
 * Хук для автоматического сохранения содержимого в RxDB
 * @param content Содержимое для сохранения
 * @param saveFn Сохраняется для совместимости, но не используется
 * @param debounceTime Время задержки (мс)
 * @param autoStart Автоматический запуск
 * @param itemId ID документа
 * @param getActualContent Функция для получения актуального контента
 * @returns Объект с функцией ручного сохранения и информацией о состоянии
 */
export function useAutoSave<T>(
  content: T,
  saveFn: (data: T, id?: string) => Promise<any>,
  debounceTime = 300,
  autoStart = true,
  itemId?: string,
  getActualContent?: () => T
) {
  const { repository, isInitialized } = useDatabase();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<Error | null>(null);
  const [lastSaveResult, setLastSaveResult] = useState<AutoSaveResult | null>(null);

  // Таймеры для debounce и синхронизации
  const saveTimerRef = useRef<number | null>(null);
  const syncTimerRef = useRef<number | null>(null);
  
  // Предыдущее сохраненное содержимое для сравнения
  const lastSavedContentRef = useRef<any>(null);
  
  // Флаг, показывающий, нужна ли синхронизация
  const needSyncRef = useRef<boolean>(false);
  
  // Блокировка во время синхронизации
  const isSyncingRef = useRef<boolean>(false);
  
  // Добавляем блокировку для предотвращения одновременных операций сохранения
  const isSavingBlockedRef = useRef<boolean>(false);
  
  // Timeout для сброса блокировки при зависаниях
  const resetBlockTimeout = useRef<number | null>(null);
  
  // Добавляем состояние для блокировки сохранения при изменении канбана
  const [kanbanEditActive, setKanbanEditActive] = useState<boolean>(false);
  const kanbanEditTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  /**
   * Функция для синхронизации с сервером
   */
  const syncWithServer = useCallback(async (): Promise<void> => {
    if (!isInitialized || !itemId || !needSyncRef.current) {
      return;
    }
    
    // Проверяем блокировку
    if (isSyncingRef.current) {
      console.log(`Синхронизация для ${itemId} уже выполняется, пропускаем`);
      return;
    }
    
    // Устанавливаем блокировку
    isSyncingRef.current = true;
    
    try {
      console.log(`Синхронизация документа ${itemId} с сервером...`);
      
      // Получаем документ из базы данных
      const doc = await repository.getDocumentById(itemId);
      
      if (!doc) {
        console.log(`Документ ${itemId} не найден в БД для синхронизации`);
        needSyncRef.current = false;
        return;
      }
      
      // Если документ уже синхронизирован, ничего не делаем
      if (doc.syncStatus === 'synced') {
        console.log(`Документ ${itemId} уже синхронизирован`);
        needSyncRef.current = false;
        return;
      }
      
      // Отправляем данные на сервер
      try {
        const result = await apiClient.saveDocument(itemId, doc.content);
        
        if (result.success) {
          // Обновляем статус синхронизации в базе данных
          const updateResult = await repository.updateSyncStatus(
            itemId,
            'synced',
            result.serverTimestamp
          );
          
          if (updateResult) {
            console.log(`Документ ${itemId} успешно синхронизирован с сервером`);
            needSyncRef.current = false;
          } else {
            console.warn(`Не удалось обновить статус синхронизации для ${itemId}, возможно из-за конфликта`);
            // Не сбрасываем флаг синхронизации, чтобы повторить позже
          }
        } else {
          console.error(`Сервер вернул ошибку при сохранении документа ${itemId}`);
        }
      } catch (apiError) {
        console.error(`Ошибка при отправке документа ${itemId} на сервер:`, apiError);
        // Не сбрасываем флаг необходимости синхронизации
      }
    } catch (error) {
      console.error(`Общая ошибка при синхронизации документа ${itemId} с сервером:`, error);
    } finally {
      // Снимаем блокировку
      isSyncingRef.current = false;
    }
  }, [itemId, repository, isInitialized]);
  
  // Функция для проверки наличия канбана в контенте
  const hasKanbanComponent = (content: any): boolean => {
    if (typeof content !== 'string') return false;
    
    return content.includes('data-type="kanban"') || 
           content.includes('data-type="kanbanBoard"') ||
           content.includes('interactive-kanban-wrapper') || 
           content.includes('kanban-board-react-content');
  };
  
  // Функция для нормализации канбан-компонентов в HTML
  const normalizeKanbanComponents = (html: string): string => {
    if (!hasKanbanComponent(html)) return html;
    
    // Используем регулярное выражение для поиска канбан-компонентов
    // и замены их на стандартный маркер
    return html.replace(
      /<div[^>]*?(data-type="kanban"|data-type="kanbanBoard"|interactive-kanban-wrapper|kanban-board-react-content)[^>]*?>[\s\S]*?<\/div>/gi,
      '<div data-type="kanban-normalized"></div>'
    );
  };
  
  // Функция для сохранения содержимого в RxDB
  const saveToDatabase = useCallback(async (): Promise<AutoSaveResult> => {
    // Получаем актуальный контент, если предоставлена функция
    var actualContent = getActualContent ? getActualContent() : content;
    
    // Если активно редактирование канбана, пропускаем сохранение
    if (kanbanEditActive) {
      console.log('[Автосохранение] Сохранение пропущено - активно редактирование канбана');
      return {
        success: true,
        timestamp: Date.now(),
        message: 'Сохранение пропущено из-за активного редактирования канбана'
      };
    }
    
    // Проверка блокировки: если сохранение уже в процессе, прерываем
    if (isSaving || isSavingBlockedRef.current) {
      return {
        success: false,
        timestamp: Date.now(),
        message: 'Сохранение уже выполняется'
      };
    }
    
    // Если нет инициализации или ID, выходим
    if (!isInitialized || !itemId) {
      console.warn('RxDB не инициализирована или отсутствует ID документа');
      return {
        success: false,
        timestamp: Date.now(),
        message: 'База данных не инициализирована или отсутствует ID документа'
      };
    }
    
    // Устанавливаем блокировку
    isSavingBlockedRef.current = true;
    
    // Устанавливаем таймаут для сброса блокировки в случае ошибки
    resetBlockTimeout.current = window.setTimeout(() => {
      isSavingBlockedRef.current = false;
    }, 5000); // 5 секунд максимальное время блокировки
    
    // Создаем более надежную проверку на изменения контента
    let isContentChanged = false;
    try {
      if (actualContent === undefined || actualContent === null) {
        console.warn('Текущий контент undefined или null, не сохраняем');
        isSavingBlockedRef.current = false;
        if (resetBlockTimeout.current) {
          clearTimeout(resetBlockTimeout.current);
        }
        return {
          success: false,
          timestamp: Date.now(),
          message: 'Содержимое не определено'
        };
      }
      
      // Улучшаем сравнение для HTML строк, нормализуя их перед сравнением
      if (typeof actualContent === 'string' && typeof lastSavedContentRef.current === 'string') {
        // Улучшенная нормализация HTML для более точного сравнения
        const normalizeHtml = (html: string) => {
          return html
            .trim()
            .replace(/\s+/g, ' ')           // Заменяем последовательности пробелов на один пробел
            .replace(/>\s+</g, '><')        // Удаляем пробелы между тегами
            .replace(/\s+>/g, '>')          // Удаляем пробелы перед закрывающей скобкой
            .replace(/<\s+/g, '<');         // Удаляем пробелы после открывающей скобки
        };
        
        // Проверяем, содержит ли контент канбан-компонент
        const hasKanban = hasKanbanComponent(actualContent) || hasKanbanComponent(lastSavedContentRef.current);
        
        let normalizedContent: string;
        let normalizedSavedContent: string;
        
        if (hasKanban) {
          // Если есть канбан, нормализуем канбан-компоненты перед сравнением
          normalizedContent = normalizeHtml(normalizeKanbanComponents(actualContent));
          normalizedSavedContent = normalizeHtml(normalizeKanbanComponents(lastSavedContentRef.current));
        } else {
          // Стандартная нормализация для документов без канбана
          normalizedContent = normalizeHtml(actualContent);
          normalizedSavedContent = normalizeHtml(lastSavedContentRef.current);
        }
        
        isContentChanged = normalizedContent !== normalizedSavedContent;
      } else {
        // Для объектов используем JSON.stringify для сравнения
        const contentJson = JSON.stringify(actualContent);
        const savedContentJson = JSON.stringify(lastSavedContentRef.current);
        isContentChanged = contentJson !== savedContentJson;
      }
    } catch (compareError) {
      // При ошибке сравнения считаем, что контент изменился
      console.warn('Ошибка при сравнении контента, считаем что он изменился:', compareError);
      isContentChanged = true;
    }
    
    // Если контент не изменился, не сохраняем
    if (!isContentChanged) {
      // Снимаем блокировку
      isSavingBlockedRef.current = false;
      if (resetBlockTimeout.current) {
        clearTimeout(resetBlockTimeout.current);
      }
      return {
        success: true,
        timestamp: Date.now(),
        message: 'Нет изменений для сохранения'
      };
    }
    
    setIsSaving(true);
    setSaveError(null);
    
    try {
      // Вызываем переданную функцию сохранения перед сохранением в базу данных
      // Это гарантирует, что если родительский компонент передал saveFn, он будет использован
      try {
        if (saveFn && typeof saveFn === 'function') {
          console.log('[AutoSave] Вызов переданной функции сохранения saveFn');
          await saveFn(actualContent, itemId);
        }
      } catch (saveFnError) {
        console.error('[AutoSave] Ошибка при вызове переданной функции сохранения:', saveFnError);
        // Продолжаем процесс, не прерывая его из-за ошибки в saveFn
      }
      
      // Сохраняем документ в RxDB
      const savedDoc = await repository.saveDocument({
        id: itemId,
        content: actualContent // Используем актуальный контент здесь
      });
      
      // Обновляем последнее сохраненное содержимое - ТОЛЬКО ПРИ УСПЕШНОМ СОХРАНЕНИИ
      // Для строк сохраняем напрямую, для объектов - копию
      if (typeof actualContent === 'string') {
        lastSavedContentRef.current = actualContent;
      } else {
        // Создаем глубокую копию объекта через JSON
        try {
          lastSavedContentRef.current = JSON.parse(JSON.stringify(actualContent));
        } catch (copyError) {
          console.warn('[ОШИБКА] Не удалось создать копию контента:', copyError);
          lastSavedContentRef.current = actualContent; // Используем оригинал
        }
      }
      
      // Также сохраняем документ на сервере через knowledgeApi
      try {
        // Получим текущие данные документа из локальной БД
        const existingDoc = await repository.getDocumentById(itemId);
        
        // Запросим данные файла через API, чтобы получить полную структуру
        let fileData: KnowledgeItem | null = null;
        
        try {
          if (itemId) {
            fileData = await knowledgeApi.getFile(itemId);
          }
        } catch (apiError) {
          console.error(`[ОШИБКА] Не удалось получить данные файла через API:`, apiError);
        }
        
        // Если получили данные из API, используем их как основу
        if (fileData) {
          // Создаем объект для сохранения, обновляя только контент
          const itemToSave: KnowledgeItem = {
            ...fileData,
            content: typeof actualContent === 'string' 
              ? actualContent 
              : JSON.stringify(actualContent)
          };
          
          // Отправляем на сервер
          const serverResponse = await knowledgeApi.save(itemToSave);
          
          if (serverResponse && serverResponse.id) {
            console.log(`[СОХРАНЕНИЕ] Документ ${itemId} успешно сохранен на сервере, ответ:`, serverResponse);
          } else {
            console.warn(`[СОХРАНЕНИЕ] Сервер вернул некорректный ответ при сохранении документа ${itemId}:`, serverResponse);
          }
        } 
        // Если не удалось получить данные через API, используем минимальные данные
        else {
          console.warn(`[СОХРАНЕНИЕ] Не удалось получить данные файла через API, используем минимальные данные`);
          
          const itemToSave: KnowledgeItem = {
            id: itemId,
            content: typeof actualContent === 'string' 
              ? actualContent 
              : JSON.stringify(actualContent),
            itemType: 'file' as const,
            name: `Документ ${itemId}`,
            fileType: 'content',
            parentId: null
          };
          
          // Отправляем на сервер
          const serverResponse = await knowledgeApi.save(itemToSave);
          
          if (serverResponse && serverResponse.id) {
            console.log(`[СОХРАНЕНИЕ] Документ ${itemId} успешно сохранен на сервере с минимальными данными, ответ:`, serverResponse);
          } else {
            console.warn(`[СОХРАНЕНИЕ] Сервер вернул некорректный ответ при сохранении документа ${itemId}:`, serverResponse);
          }
        }
      } catch (serverError) {
        console.error(`[ОШИБКА] Не удалось сохранить документ ${itemId} на сервере:`, serverError);
        // Продолжаем выполнение, так как локальное сохранение уже прошло успешно
      }
      
      const result = {
        success: true,
        timestamp: savedDoc.localTimestamp,
        message: 'Сохранено локально и отправлено на сервер'
      };
      
      console.log('Документ успешно сохранен в RxDB:', result);
      setLastSaveResult(result);
      
      // Устанавливаем флаг необходимости синхронизации и планируем её
      needSyncRef.current = true;
      
      // Отменяем предыдущий таймер синхронизации, если он существует
      if (syncTimerRef.current !== null) {
        window.clearTimeout(syncTimerRef.current);
      }
      
      // Запускаем синхронизацию с задержкой
      syncTimerRef.current = window.setTimeout(() => {
        syncWithServer();
      }, 2000); // 2 секунды задержка для синхронизации
      
      // Снимаем блокировку
      isSavingBlockedRef.current = false;
      if (resetBlockTimeout.current) {
        clearTimeout(resetBlockTimeout.current);
      }
      
      return result;
    } catch (error) {
      console.error('Ошибка при сохранении в RxDB:', error);
      setSaveError(error instanceof Error ? error : new Error(String(error)));
      
      const result = {
        success: false,
        timestamp: Date.now(),
        message: `Ошибка сохранения: ${error instanceof Error ? error.message : String(error)}`
      };
      
      setLastSaveResult(result);
      
      // Снимаем блокировку
      isSavingBlockedRef.current = false;
      if (resetBlockTimeout.current) {
        clearTimeout(resetBlockTimeout.current);
      }
      
      return result;
    } finally {
      setIsSaving(false);
    }
  }, [content, itemId, repository, isInitialized, syncWithServer, isSaving, getActualContent, kanbanEditActive]);
  
  // Функция для запуска автосохранения с debounce
  const debouncedSave = useCallback(() => {
    // Сначала очищаем предыдущий таймер, если он существует
    if (saveTimerRef.current !== null) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    
    // Создаем новый таймер
    saveTimerRef.current = window.setTimeout(() => {
      saveToDatabase();
    }, debounceTime);
  }, [saveToDatabase, debounceTime]);
  
  // Отслеживаем изменения контента для автосохранения
  useEffect(() => {
    if (autoStart && isInitialized && itemId) {
      // Добавляем явный лог текущего контента при запуске автосохранения
      console.log(`[АВТОСОХРАНЕНИЕ] Запущено сохранение для документа ${itemId}`);
      console.log(`[АВТОСОХРАНЕНИЕ] Текущее содержимое:`, typeof content === 'string' 
        ? content.substring(0, 100) + '...' 
        : JSON.stringify(content).substring(0, 100) + '...');
        
      debouncedSave();
    }
    
    // Очистка таймера при размонтировании
    return () => {
      if (saveTimerRef.current !== null) {
        window.clearTimeout(saveTimerRef.current);
      }
      
      if (syncTimerRef.current !== null) {
        window.clearTimeout(syncTimerRef.current);
      }
    };
  }, [content, debouncedSave, isInitialized, itemId, autoStart]);
  
  // Функция для принудительного сохранения
  const forceSave = useCallback(async (): Promise<AutoSaveResult> => {
    console.log('[ПРИНУДИТЕЛЬНОЕ СОХРАНЕНИЕ] Запрошено принудительное сохранение');
    console.log('[ПРИНУДИТЕЛЬНОЕ СОХРАНЕНИЕ] Текущее содержимое:', typeof content === 'string' 
      ? content.substring(0, 100) + '...' 
      : JSON.stringify(content).substring(0, 100) + '...');
    
    // Очищаем таймеры, если они существуют
    if (saveTimerRef.current !== null) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    
    if (syncTimerRef.current !== null) {
      window.clearTimeout(syncTimerRef.current);
      syncTimerRef.current = null;
    }
    
    // Сохраняем в базу данных
    const saveResult = await saveToDatabase();
    
    // Сразу же синхронизируем с сервером, без задержки
    if (saveResult.success) {
      await syncWithServer();
    }
    
    return saveResult;
  }, [saveToDatabase, syncWithServer, content]);
  
  // Синхронизируем при размонтировании, если есть несохраненные изменения
  useEffect(() => {
    return () => {
      if (needSyncRef.current && itemId && isInitialized) {
        // Форсируем синхронизацию перед размонтированием
        syncWithServer();
      }
    };
  }, [syncWithServer, itemId, isInitialized]);
  
  // Логируем инициализацию
  useEffect(() => {
    console.log(`Инициализирован хук автосохранения RxDB для документа ${itemId}`);
    
    // Проверяем при инициализации, не требуется ли загрузить данные
    const loadInitialContent = async () => {
      if (itemId && isInitialized) {
        // Загружаем документ из базы данных
        const doc = await repository.getDocumentById(itemId);
        if (doc) {
          // Если есть документ, обновляем ссылку на последнее сохраненное содержимое
          lastSavedContentRef.current = doc.content;
          console.log(`Загружены данные для документа ${itemId} из локальной БД`);
        }
      }
    };
    
    loadInitialContent();
    
    return () => {
      console.log(`Хук автосохранения RxDB для документа ${itemId} удален`);
    };
  }, [itemId, repository, isInitialized]);
  
  // Инициализация lastSavedContentRef при первоначальной загрузке
  useEffect(() => {
    // Инициализируем lastSavedContentRef текущим значением контента только при смене itemId
    // И ТОЛЬКО при первой загрузке документа
    if (itemId && !lastSavedContentRef.current) {
      console.log(`Первичная инициализация lastSavedContentRef для документа ${itemId}`);
      
      // Асинхронно загружаем данные из репозитория для более точной инициализации
      const loadSavedContent = async () => {
        try {
          if (isInitialized) {
            const doc = await repository.getDocumentById(itemId);
            if (doc && doc.content) {
              console.log(`Найден документ в БД для инициализации lastSavedContentRef: ${itemId}`);
              if (typeof doc.content === 'string') {
                lastSavedContentRef.current = doc.content;
                console.log(`Установлено начальное значение lastSavedContentRef из БД (строка): ${doc.content.substring(0, 30)}...`);
              } else {
                try {
                  lastSavedContentRef.current = JSON.parse(JSON.stringify(doc.content));
                  console.log(`Установлено начальное значение lastSavedContentRef из БД (объект): ${JSON.stringify(doc.content).substring(0, 30)}...`);
                } catch (e) {
                  lastSavedContentRef.current = doc.content;
                  console.warn(`Не удалось создать копию контента из БД, используется прямая ссылка`, e);
                }
              }
              // Возвращаемся, т.к. загрузили контент из БД
              return;
            }
          }
          
          // Если не удалось загрузить из БД, используем current content как запасной вариант
          if (content !== undefined && content !== null) {
            if (typeof content === 'string') {
              lastSavedContentRef.current = content;
              console.log(`Установлено начальное значение lastSavedContentRef из props (строка): ${content.substring(0, 30)}...`);
            } else {
              try {
                lastSavedContentRef.current = JSON.parse(JSON.stringify(content));
                console.log(`Установлено начальное значение lastSavedContentRef из props (объект): ${JSON.stringify(content).substring(0, 30)}...`);
              } catch (e) {
                lastSavedContentRef.current = content;
                console.warn(`Не удалось создать копию контента из props, используется прямая ссылка`, e);
              }
            }
          }
        } catch (error) {
          console.error('Ошибка при инициализации lastSavedContentRef:', error);
          // Если возникла ошибка, используем current content как запасной вариант
          if (content !== undefined && content !== null) {
            lastSavedContentRef.current = content;
          }
        }
      };
      
      loadSavedContent();
    }
  }, [itemId, isInitialized, repository]);

  // Добавляем эффект для обработки события канбана
  useEffect(() => {
    // Обработчик события изменения канбана
    const handleKanbanChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('[Автосохранение] Получено событие kanban-state-change, блокируем сохранение');
      
      // Активируем блокировку сохранения
      setKanbanEditActive(true);
      
      // Сбрасываем предыдущий таймер
      if (kanbanEditTimeoutRef.current) {
        clearTimeout(kanbanEditTimeoutRef.current);
      }
      
      // Устанавливаем таймер для снятия блокировки
      kanbanEditTimeoutRef.current = setTimeout(() => {
        console.log('[Автосохранение] Блокировка сохранения из-за канбана снята');
        setKanbanEditActive(false);
      }, 5000); // Блокируем на 5 секунд
    };
    
    // Добавляем обработчик событий на весь документ
    document.addEventListener('kanban-state-change', handleKanbanChange);
    
    // Удаляем обработчик при размонтировании
    return () => {
      document.removeEventListener('kanban-state-change', handleKanbanChange);
      
      // Очищаем таймер
      if (kanbanEditTimeoutRef.current) {
        clearTimeout(kanbanEditTimeoutRef.current);
      }
    };
  }, []);

  // Возвращаем объект с функциями и состоянием автосохранения
  return {
    forceSave,
    isSaving,
    saveError,
    lastSaveResult
  };
} 